/**
 * @packageDocumentation
 * @module      MsalMain.ts
 * @description Triggers the Microsoft Authentication Redirection
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var MSALComponent_1;
import * as msal from '@azure/msal-browser';
import { EventType } from '@azure/msal-browser';
import { customElement, html, internalProperty, LitElement } from 'lit-element';
import getPkce from 'oauth-pkce';
import { interpret } from 'xstate';
import { MSALredirectionStateMachine, dispatchEventToMSALComponent, getStateValueForStateNode } from "./MsalStateMachine";
import LoggedInUser from "./LoggedInUser";
import CorThemeSelector from './CorThemeSelector';
import CloudApi from "./CloudApi";
import ConsoleLogger from './ConsoleLogger';
import Fetcher from "./Fetcher";
export let msalMainFetcher = new Fetcher();
const msalRedirectionStateMachine = interpret(MSALredirectionStateMachine).start();
const authApp = new msal.PublicClientApplication(appConfig.msalConfig);
const refreshTokenInterval = 30 * 60 * 1000; //30 mins 
authApp.addEventCallback((event) => {
    if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || event.eventType === EventType.LOGIN_SUCCESS) {
        msalRedirectionStateMachine.send({
            type: 'LOGIN_SUCCESS'
        });
    }
    else if (event.eventType == EventType.ACQUIRE_TOKEN_FAILURE || event.eventType === EventType.LOGIN_FAILURE) {
        msalRedirectionStateMachine.send({
            type: 'LOGIN_FAILURE'
        });
    }
    else if (event.eventType == EventType.HANDLE_REDIRECT_END) {
        msalRedirectionStateMachine.send({
            type: 'HANDLE_REDIRECT_END'
        });
    }
});
let MSALComponent = MSALComponent_1 = class MSALComponent extends LitElement {
    constructor() {
        super();
        this.getSessionManagerVersion = () => __awaiter(this, void 0, void 0, function* () {
            try {
                MSALComponent_1.sessionManagerVersion = yield new CloudApi(msalMainFetcher).getVersion();
            }
            catch (error) {
                ConsoleLogger.log('Failed to fetch the Session Manager version: ' + error);
            }
        });
        this.msalState = MSALredirectionStateMachine.config.initial;
        this.loggedInUser = LoggedInUser;
        this.setMsalState = (e) => {
            this.msalState = e.detail;
        };
        this.getSessionManagerVersion().then(() => {
            return this.requestUpdate();
        });
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('msalStateChanged', this.setMsalState);
    }
    render() {
        const defaultDisplay = html `
            <p> Loading. Please wait... </p>
        `;
        const loginErrorDisplay = html `
            <cor-login loginResultCode=${MSALredirectionStateMachine.states.login_failure}></cor-login>
        `;
        if (MSALComponent_1.sessionManagerVersion != "") {
            if (MSALComponent_1.sessionManagerVersion != appConfig.systemsManagerVersion) {
                return loginErrorDisplay;
            }
            else {
                MSALComponent_1.hasVersionMismatch = false;
                switch (this.msalState) {
                    case getStateValueForStateNode(MSALredirectionStateMachine.states.in_login):
                        return html `
                        <cor-login></cor-login>
                        `;
                    case getStateValueForStateNode(MSALredirectionStateMachine.states.login_failure):
                        return loginErrorDisplay;
                    case getStateValueForStateNode(MSALredirectionStateMachine.states.login_success):
                        return html `
                        <cor-main></cor-main>
                        `;
                    default:
                        return defaultDisplay;
                }
            }
        }
        else {
            return defaultDisplay;
        }
    }
};
MSALComponent.sessionManagerVersion = "";
MSALComponent.hasVersionMismatch = true;
__decorate([
    internalProperty()
], MSALComponent.prototype, "getSessionManagerVersion", void 0);
__decorate([
    internalProperty()
], MSALComponent.prototype, "msalState", void 0);
__decorate([
    internalProperty()
], MSALComponent.prototype, "loggedInUser", void 0);
MSALComponent = MSALComponent_1 = __decorate([
    customElement('msal-component')
], MSALComponent);
export { MSALComponent };
const pkceCodes = {
    codeVerifier: "",
    codeChallenge: "",
    codeChallengeMethod: 'S256'
};
// create a verifier of 43 characters long
getPkce(43, (error, { verifier, challenge }) => {
    if (!error) {
        pkceCodes.codeVerifier = verifier;
        pkceCodes.codeChallenge = challenge;
    }
    else {
        ConsoleLogger.log("Error in creating a code verifier and code challenge.");
    }
});
const authorizationRequest = {
    scopes: appConfig.msalScopes,
    codeChallenge: pkceCodes.codeChallenge,
    codeChallengeMethod: 'S256',
    prompt: 'login'
};
//acquire the access token using the refresh token 
function refreshAccessToken() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const account = authApp.getActiveAccount();
            if (account) {
                const silentRequest = {
                    scopes: appConfig.msalScopes,
                    account: account,
                    forceRefresh: true,
                    refreshTokenExpirationOffsetSeconds: 84600 //23.5 hours
                };
                authApp.acquireTokenSilent(silentRequest).
                    then((response) => {
                    handleAuthenticationResponse(response);
                })
                    .catch((error) => {
                    console.error('There is an error in fetching the token' + error);
                });
            }
            else {
                console.error("No active account found");
            }
        }
        catch (error) {
            handleAuthenticationError(error);
        }
    });
}
function handleAuthenticationResponse(response) {
    return __awaiter(this, void 0, void 0, function* () {
        const state = msalRedirectionStateMachine.getSnapshot().value;
        if (state == "login_success") {
            const accounts = authApp.getAllAccounts();
            if (accounts) {
                authApp.setActiveAccount(accounts[0]);
            }
            if (response === null || response === void 0 ? void 0 : response.idToken) {
                LoggedInUser.populateLoggedInUser(response.idTokenClaims.email, response.idTokenClaims.name, response.accessToken).then(() => {
                    dispatchEventToMSALComponent(state);
                });
            }
        }
        else {
            dispatchEventToMSALComponent(state);
        }
    });
}
authApp.handleRedirectPromise()
    .then((response) => {
    CorThemeSelector.initializeTheme();
    handleAuthenticationResponse(response);
    setInterval(() => {
        refreshAccessToken();
    }, refreshTokenInterval);
})
    .catch((error) => {
    console.error('Error handling MSAL redirection:', error);
});
export function msLogIn() {
    authApp.loginRedirect(authorizationRequest);
}
export function msLogOut() {
    const logoutRequest = {
        account: authApp.getActiveAccount(),
    };
    authApp.logoutRedirect(logoutRequest);
}
function handleAuthenticationError(error) {
    console.error('Error handling MSAL redirection:', error);
}
//# sourceMappingURL=MsalMain.js.map